import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { getTrialInfo } from '../authStorage';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import LanguageSelector from '../i18n/LanguageSelector';
import { useBranchFilter, isCeoUser } from '../branchFilter';
import { fetchBranches } from '../branchesApi';
import './Navbar.css';

const PAGE_TITLE_KEYS = {
  dashboard:        'nav.officerDashboard',
  branches:         'nav.branches',
  board:            'nav.board',
  committees:       'nav.committees',
  shareholders:     'nav.shareholders',
  decisions:        'common.decisions',
  'decision-types': 'nav.decisionTypes',
  meetings:         'common.meetings',
  voting:           'nav.voting',
  permissions:      'nav.myPermissions',
  settings:         'common.settings',
};

const PAGE_ICONS = {
  dashboard: 'dashboard', branches: 'branch', board: 'board', committees: 'board',
  shareholders: 'money', decisions: 'decisions', 'decision-types': 'decisions',
  meetings: 'calendar', voting: 'voting',
  permissions: 'lock', settings: 'settings',
};

const NOTIFS = [
  { id: 1, textKey: 'notif.newDecision', timeKey: '5 min', dot: '#02c3a6' },
  { id: 2, textKey: 'notif.permsUpdated', timeKey: '20 min', dot: '#f57c00' },
];

export default function OfficerNavbar({ activePage, onToggleSidebar }) {
  const { language } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const { branches, selectedBranch, setSelectedBranch, setBranches } = useBranchFilter();

  // Fetch branches here — OfficerNavbar only mounts after login, so auth is guaranteed
  useEffect(() => {
    if (!isCeoUser()) return;
    fetchBranches().then(setBranches).catch(() => {});
  }, [setBranches]);

  const pageTitle = t(PAGE_TITLE_KEYS[activePage] ?? PAGE_TITLE_KEYS.dashboard);
  const metaIcon  = PAGE_ICONS[activePage] ?? PAGE_ICONS.dashboard;

  const [dropOpen, setDropOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const [trialRemainingMs, setTrialRemainingMs] = useState(null);
  const dropRef = useRef(null);
  const notifRef = useRef(null);

  const todayStr = useMemo(() => new Date().toLocaleDateString(
    language === 'ar' || language === 'ur' ? 'ar-SA-u-ca-gregory'
      : language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-US',
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
  ), [language]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const trial = getTrialInfo();
    if (!trial?.trialEndsAtUtc) { setTrialRemainingMs(null); return undefined; }
    const endMs = new Date(trial.trialEndsAtUtc).getTime();
    const tick = () => setTrialRemainingMs(Math.max(0, endMs - Date.now()));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  const trialLabel = (() => {
    if (trialRemainingMs == null) return '';
    const totalSec = Math.ceil(trialRemainingMs / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    const mmss = `${String(min).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    return trialRemainingMs > 0
      ? `${t('nav.trialExpires')} ${mmss}`
      : t('nav.trialEnded');
  })();

  return (
    <header className="navbar">
      <div className="navbar__accent" />

      <button className="navbar__hamburger" onClick={onToggleSidebar} aria-label={t('common.openMenu')}>
        <span /><span /><span />
      </button>

      <div className="navbar__identity">
        <div className="navbar__page-badge">
          <Icon name={metaIcon} size={17} color="#fff" strokeWidth={1.8} />
        </div>
        <div className="navbar__identity-text">
          <span className="navbar__title">{pageTitle}</span>
          <div className="navbar__breadcrumb">
            <span>{t('common.brand')}</span>
            <Icon name="chevron-left" size={9} color="#cfd8dc" />
            <span>{pageTitle}</span>
          </div>
        </div>
      </div>

      {trialRemainingMs != null && (
        <div className={`navbar__trial-alert${trialRemainingMs <= 60_000 ? ' navbar__trial-alert--urgent' : ''}`}>
          <Icon name="warning" size={13} color={trialRemainingMs <= 60_000 ? '#fff' : '#8a4b00'} />
          <span>{trialLabel}</span>
        </div>
      )}

      <div className={`navbar__search${searchVal ? ' navbar__search--filled' : ''}`}>
        <Icon name="search" size={14} color="#90a4ae" className="navbar__search-icon" />
        <input
          className="navbar__search-input"
          type="text"
          placeholder={t('nav.secretariesSearch')}
          value={searchVal}
          onChange={(e) => setSearchVal(e.target.value)}
        />
        {searchVal
          ? <button className="navbar__search-clear" onClick={() => setSearchVal('')}>✕</button>
          : <span className="navbar__search-hint">⌘K</span>
        }
      </div>

      <div className="navbar__controls">
        {branches.length > 0 && (
          <div className="navbar__branch-filter">
            <Icon name="branch" size={13} color="#02c3a6" />
            <select
              className="navbar__branch-select"
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
            >
              <option value="all">{t('branches.allBranches')}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.name}>{b.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="navbar__date-chip">
          <Icon name="calendar" size={13} color="#02c3a6" />
          <span>{todayStr}</span>
        </div>
        <div className="navbar__sep" />

        <div className="navbar__notif-wrap" ref={notifRef}>
          <button
            className={`navbar__ctrl-btn${notifOpen ? ' navbar__ctrl-btn--active' : ''}`}
            onClick={() => { setNotifOpen((o) => !o); setDropOpen(false); }}
            title={t('common.notifications')}
          >
            <Icon name="bell" size={17} color={notifOpen ? '#02c3a6' : '#546e7a'} />
            <span className="navbar__badge">{NOTIFS.length}</span>
          </button>
          {notifOpen && (
            <div className="navbar__notif-panel">
              <div className="navbar__notif-panel-head">
                <span className="navbar__notif-panel-title">{t('common.notifications')}</span>
              </div>
              <div className="navbar__notif-list">
                {NOTIFS.map((n) => (
                  <div key={n.id} className="navbar__notif-item">
                    <span className="navbar__notif-dot" style={{ background: n.dot }} />
                    <div className="navbar__notif-body">
                      <div className="navbar__notif-text">{n.textKey}</div>
                      <div className="navbar__notif-time">{n.timeKey}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button className="navbar__ctrl-btn" title={t('common.settings')} onClick={() => navigate('/officer/settings')}>
          <Icon name="settings" size={17} color="#546e7a" />
        </button>

        <LanguageSelector variant="icon" placement="bottom-end" />

        <div className="navbar__sep" />

        <div className="navbar__user-wrap" ref={dropRef}>
          <button className={`navbar__user${dropOpen ? ' navbar__user--open' : ''}`}
            onClick={() => { setDropOpen((o) => !o); setNotifOpen(false); }}>
            <div className="navbar__avatar-ring"><div className="navbar__avatar">أس</div></div>
            <div className="navbar__user-info">
              <span className="navbar__user-name">{t('nav.officer')}</span>
              <span className="navbar__user-role">{t('nav.operationalAccount')}</span>
            </div>
            <Icon name={dropOpen ? 'chevron-up' : 'chevron-down'} size={10} color="#90a4ae" />
          </button>
          {dropOpen && (
            <div className="navbar__dropdown">
              <div className="navbar__drop-header">
                <div className="navbar__drop-avatar">أس</div>
                <div>
                  <div className="navbar__drop-name">{t('nav.officer')}</div>
                  <div className="navbar__drop-role">{t('nav.operationalAccount')}</div>
                </div>
              </div>
              <div className="navbar__drop-body">
                <button className="navbar__drop-item" onClick={() => { setDropOpen(false); navigate('/officer/settings'); }}>
                  <span className="navbar__drop-item-icon"><Icon name="settings" size={14} color="#546e7a" /></span>
                  {t('common.accountSettings')}
                </button>
                <div className="navbar__drop-sep" />
                <button className="navbar__drop-item navbar__drop-item--danger" onClick={() => { setDropOpen(false); navigate('/login'); }}>
                  <span className="navbar__drop-item-icon"><Icon name="log-out" size={14} color="#c62828" /></span>
                  {t('common.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
